import { useState, useEffect, useMemo } from 'react'
import { Info, ChevronUp, ChevronDown } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts'
import { useTranslation } from 'react-i18next'
import i18n from '../../../i18n'
import { leadsAPI } from '../api'
import type { HendelserResponse, HendelseRow } from '../api'
import { formatNorwegianDate } from '../../../utils/dateUtils'
import LeadsTableExport from '../LeadsTableExport'
import { LEADS_EXPORT_LIMIT } from '../eksportTilgang'
import type { ColumnDefinition } from '../../../utils/exportToCsv'

interface Props {
  orgNr: string
  /** For the export's title (#885). */
  orgNavn?: string | null
}

type TypeKey = 'eie' | 'leasing' | 'solgt' | 'kjopt' | 'vraket' | 'utfort'

const TYPE_BADGE: Record<string, { labelKey: `orgHendelser.types.${TypeKey}`; style: string }> = {
  eie: { labelKey: 'orgHendelser.types.eie', style: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' },
  leasing: { labelKey: 'orgHendelser.types.leasing', style: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300' },
  solgt: { labelKey: 'orgHendelser.types.solgt', style: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' },
  kjopt: { labelKey: 'orgHendelser.types.kjopt', style: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' },
  vraket: { labelKey: 'orgHendelser.types.vraket', style: 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200' },
  utfort: { labelKey: 'orgHendelser.types.utfort', style: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300' },
}

const SOURCE_BADGE: Record<string, { labelKey: `orgHendelser.sources.${'nyreg' | 'bruktbil' | 'endstate'}`; style: string }> = {
  nyreg: { labelKey: 'orgHendelser.sources.nyreg', style: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300' },
  bruktbil: { labelKey: 'orgHendelser.sources.bruktbil', style: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300' },
  endstate: { labelKey: 'orgHendelser.sources.endstate', style: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300' },
}

const TYPE_COLORS = {
  eie: '#3b82f6',
  leasing: '#8b5cf6',
  solgt: '#ef4444',
  kjopt: '#22c55e',
  vraket: '#6b7280',
  utfort: '#f97316',
}

type SortKey = 'dato' | 'type' | 'kjennemerke' | 'merke' | 'modell' | 'source' | 'sted'
type SortDir = 'asc' | 'desc'

const PAGE_SIZE = 50

function formatDate24MonthsAgo(): string {
  const d = new Date()
  d.setMonth(d.getMonth() - 24)
  return d.toISOString().slice(0, 10)
}

function formatToday(): string {
  return new Date().toISOString().slice(0, 10)
}

function getDisplayDate(row: HendelseRow): string {
  return row.hendelse_tidspunkt ? row.hendelse_tidspunkt.slice(0, 10) : row.registrert
}

type MonthCounts = { eie: number; leasing: number; solgt: number; kjopt: number; vraket: number; utfort: number }

function buildMonthlyData(rows: HendelseRow[]): ({ month: string } & MonthCounts)[] {
  const buckets: Record<string, MonthCounts> = {}

  for (const row of rows) {
    const date = getDisplayDate(row)
    const month = date.slice(0, 7) // YYYY-MM
    if (!buckets[month]) buckets[month] = { eie: 0, leasing: 0, solgt: 0, kjopt: 0, vraket: 0, utfort: 0 }
    buckets[month][row.type]++
  }

  return Object.entries(buckets)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, counts]) => ({ month, ...counts }))
}

/** The table's type filter and sort, applied to the loaded rows. */
function filterAndSort(all: HendelseRow[], typeFilter: string, sortKey: SortKey, sortDir: SortDir): HendelseRow[] {
  const rows = typeFilter ? all.filter(r => r.type === typeFilter) : [...all]
  const dir = sortDir === 'asc' ? 1 : -1

  rows.sort((a, b) => {
    let av: string, bv: string
    switch (sortKey) {
      case 'dato':
        av = getDisplayDate(a)
        bv = getDisplayDate(b)
        break
      case 'type':
        av = a.type
        bv = b.type
        break
      case 'kjennemerke':
        av = a.kjennemerke ?? ''
        bv = b.kjennemerke ?? ''
        break
      case 'merke':
        av = a.merke ?? ''
        bv = b.merke ?? ''
        break
      case 'modell':
        av = a.modell ?? ''
        bv = b.modell ?? ''
        break
      case 'source':
        av = a.source
        bv = b.source
        break
      case 'sted':
        av = a.postnummer ?? ''
        bv = b.postnummer ?? ''
        break
      default:
        return 0
    }
    return av < bv ? -dir : av > bv ? dir : 0
  })
  return rows
}

export default function HendelserTab({ orgNr, orgNavn }: Props) {
  const { t } = useTranslation('leads')
  const [data, setData] = useState<HendelserResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sortKey, setSortKey] = useState<SortKey>('dato')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [typeFilter, setTypeFilter] = useState('')
  const [page, setPage] = useState(1)

  // Back to page 1 when the visible set changes shape
  useEffect(() => { setPage(1) }, [typeFilter, sortKey, sortDir])

  useEffect(() => {
    setLoading(true)
    setError(null)
    leadsAPI.getOrgHendelser(orgNr, formatDate24MonthsAgo(), formatToday())
      .then(res => setData(res.data))
      .catch(() => setError(i18n.t('orgHendelser.loadFailed', { ns: 'leads' })))
      .finally(() => setLoading(false))
  }, [orgNr])

  const sortedRows = useMemo(
    () => (data ? filterAndSort(data.rows, typeFilter, sortKey, sortDir) : []),
    [data, sortKey, sortDir, typeFilter],
  )

  const pages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE))
  const pageRows = useMemo(
    () => sortedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sortedRows, page],
  )

  const monthlyData = useMemo(() => {
    if (!data) return []
    return buildMonthlyData(data.rows)
  }, [data])

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir(key === 'dato' ? 'desc' : 'asc')
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse h-16 bg-gray-100 dark:bg-dark-border rounded-sm" />
        <div className="animate-pulse h-48 bg-gray-100 dark:bg-dark-border rounded-sm" />
      </div>
    )
  }

  if (error) {
    return <div className="card p-6 text-red-600 dark:text-red-400">{error}</div>
  }

  if (!data || data.summary.total_cars === 0) {
    return (
      <div className="card p-6 text-gray-500 dark:text-gray-400">
        <Info className="w-5 h-5 inline mr-2" />
        {t('orgHendelser.empty')}
      </div>
    )
  }

  const { summary } = data

  // The table holds the first 500 events. When the org has more, the export
  // fetches up to LEADS_EXPORT_LIMIT and applies the same filter and sort.
  const exportLoad = data.rows.length < summary.total_cars
    ? () => leadsAPI.getOrgHendelser(orgNr, formatDate24MonthsAgo(), formatToday(), LEADS_EXPORT_LIMIT)
      .then(res => filterAndSort(res.data.rows, typeFilter, sortKey, sortDir))
    : undefined
  const exportColumns: ColumnDefinition[] = [
    { key: 'registrert', label: t('common.dato'), format: (_v, row) => formatNorwegianDate(getDisplayDate(row)) },
    { key: 'type', label: t('common.type'), format: v => t((TYPE_BADGE[v] || TYPE_BADGE.eie).labelKey) },
    { key: 'kjennemerke', label: t('common.kjennemerke'), format: v => v ?? '—' },
    { key: 'merke', label: t('common.merke'), format: v => v ?? '—' },
    { key: 'modell', label: t('common.modell'), format: v => v ?? '—' },
    { key: 'source', label: t('common.kilde'), format: v => t((SOURCE_BADGE[v] || SOURCE_BADGE.nyreg).labelKey) },
    { key: 'postnummer', label: t('orgHendelser.sted'), format: (_v, row) => (row.postnummer ? `${row.postnummer} ${row.poststed || ''}`.trim() : '—') },
  ]

  return (
    <div className="space-y-6">
      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="card p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400">{t('orgHendelser.summary.eie')}</div>
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{summary.eie_count}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400">{t('orgHendelser.summary.leasing')}</div>
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{summary.leasing_count}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400">{t('orgHendelser.summary.solgt')}</div>
          <div className="text-2xl font-bold text-red-600 dark:text-red-400">{summary.solgt_count}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400">{t('orgHendelser.summary.kjopt')}</div>
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">{summary.kjopt_count}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400">{t('orgHendelser.types.vraket')}</div>
          <div className="text-2xl font-bold text-gray-600 dark:text-gray-300">{summary.vraket_count ?? 0}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400">{t('orgHendelser.types.utfort')}</div>
          <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{summary.utfort_count ?? 0}</div>
        </div>
      </div>

      {/* Monthly activity chart */}
      {monthlyData.length > 1 && (
        <div className="card p-6">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">{t('orgHendelser.chartTitle')}</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={monthlyData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11 }}
                tickFormatter={v => { const [y, m] = v.split('-'); return `${m}/${y.slice(2)}` }}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={30} />
              <Tooltip
                labelFormatter={v => { const [y, m] = v.split('-'); return `${m}/${y}` }}
                contentStyle={{ fontSize: 12, borderRadius: 6 }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="eie" name={t('orgHendelser.types.eie')} stroke={TYPE_COLORS.eie} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="leasing" name={t('orgHendelser.types.leasing')} stroke={TYPE_COLORS.leasing} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="solgt" name={t('orgHendelser.types.solgt')} stroke={TYPE_COLORS.solgt} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="kjopt" name={t('orgHendelser.types.kjopt')} stroke={TYPE_COLORS.kjopt} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="vraket" name={t('orgHendelser.types.vraket')} stroke={TYPE_COLORS.vraket} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="utfort" name={t('orgHendelser.types.utfort')} stroke={TYPE_COLORS.utfort} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Event table */}
      <div className="card p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white">
            {t('orgHendelser.tableTitle')}
            <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
              ({typeFilter ? t('orgHendelser.shownOf', { shown: sortedRows.length, total: summary.total_cars }) : t('common.totalCount', { n: summary.total_cars })})
            </span>
          </h3>
          <div className="flex items-center gap-3">
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              className="input-field text-sm"
            >
              <option value="">{t('common.allTypes')}</option>
              {Object.entries(TYPE_BADGE).map(([key, badge]) => (
                <option key={key} value={key}>{t(badge.labelKey)}</option>
              ))}
            </select>
            {(typeFilter || sortKey !== 'dato' || sortDir !== 'desc') && (
              <button
                onClick={() => { setTypeFilter(''); setSortKey('dato'); setSortDir('desc') }}
                className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline whitespace-nowrap"
              >
                {t('common.reset')}
              </button>
            )}
          </div>
        </div>
        <LeadsTableExport
          rows={sortedRows}
          columns={exportColumns}
          title={t('tableExport.orgTitle', { table: t('orgHendelser.tableTitle'), navn: orgNavn ?? orgNr, orgNr })}
          loadData={exportLoad}
          capped={summary.total_cars > LEADS_EXPORT_LIMIT}
          testId="bedrift-hendelser-export"
        />
        <div className="overflow-x-auto">
          <table className="data-table data-table-compact">
            <thead>
              <tr>
                <SortHeader label={t('common.dato')} sortKey="dato" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label={t('common.type')} sortKey="type" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label={t('common.kjennemerke')} sortKey="kjennemerke" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label={t('common.merke')} sortKey="merke" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label={t('common.modell')} sortKey="modell" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label={t('common.kilde')} sortKey="source" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label={t('orgHendelser.sted')} sortKey="sted" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row, idx) => {
                const displayDate = getDisplayDate(row)
                const typeBadge = TYPE_BADGE[row.type] || TYPE_BADGE.eie
                const sourceBadge = SOURCE_BADGE[row.source] || SOURCE_BADGE.nyreg
                return (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}>
                    <td className="text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {formatNorwegianDate(displayDate)}
                    </td>
                    <td>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium ${typeBadge.style}`}>
                        {t(typeBadge.labelKey)}
                      </span>
                    </td>
                    <td className="font-mono text-xs text-primary-600 dark:text-primary-400">{row.kjennemerke ?? '—'}</td>
                    <td className="text-sm">{row.merke ?? '—'}</td>
                    <td className="text-sm">{row.modell ?? '—'}</td>
                    <td>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs ${sourceBadge.style}`}>
                        {t(sourceBadge.labelKey)}
                      </span>
                    </td>
                    <td className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {row.postnummer ? `${row.postnummer} ${row.poststed || ''}` : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mt-4">
            <span>{t('common.pageOf', { page, pages })}</span>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                {t('common.prev')}
              </button>
              <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
                className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function SortHeader({ label, sortKey, currentKey, dir, onSort }: {
  label: string
  sortKey: SortKey
  currentKey: SortKey
  dir: SortDir
  onSort: (key: SortKey) => void
}) {
  const active = sortKey === currentKey
  return (
    <th
      className="text-left whitespace-nowrap cursor-pointer select-none hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          dir === 'asc'
            ? <ChevronUp className="w-3 h-3" />
            : <ChevronDown className="w-3 h-3" />
        ) : (
          <span className="w-3 h-3 inline-block" />
        )}
      </span>
    </th>
  )
}
